/**
 * Vehicle service — vehicle search, nearby, favorites, and host CRUD.
 * Class-based singleton per architecture rules.
 */
import { supabase } from '../supabase'
import type { Cols, EmbedMany, EmbedOne, Row, RpcRow, UpdateRow, WithEmbed } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import type { NearbyVehicle, SearchVehicle, VehicleSearchFilters } from '../types/vehicle.types'
import type {
  HostVehicle,
  VehicleMakeLookup,
  VehicleModelLookup,
  CreateVehicleInput,
  UpdateVehicleInput,
  VehicleOwnershipType,
  VehicleOwnershipDeclaration,
} from '../types/vehicle.types'
import type {
  VehicleDetail,
  VehicleDetailImage,
  VehicleHostSummary,
  VehicleReviewItem,
} from '../types/vehicle-detail.types'
import { EARLY_ACCESS } from '../utils/constants'
import { normalizePlate } from '../utils/sanitize'
import { formatMarketTransmissionText } from '../utils/transmission'
import { normalizeVehicleMinRentalHours } from '../utils/min-rental-duration'
import { sortVehicleImagesForShowcase } from '../types/listing-photo.types'
import { requireLiveUserId } from './session-identity'

// ─── PostGIS parser ─────────────────────────────────────────────────────────

function parsePostGISPoint(raw: unknown): { lat: number; lng: number } | null {
  if (!raw) return null

  const parseFinitePoint = (lng: number, lat: number) => {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
    return { lng, lat }
  }

  const parseWkbHexPoint = (value: string) => {
    const hex = value.trim()
    if (!/^[0-9a-fA-F]+$/.test(hex) || hex.length % 2 !== 0 || hex.length < 42) return null

    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16)
    }

    const view = new DataView(bytes.buffer)
    const endianFlag = view.getUint8(0)
    if (endianFlag !== 0 && endianFlag !== 1) return null

    const littleEndian = endianFlag === 1
    const type = view.getUint32(1, littleEndian)
    const geometryType = type & 0xff
    const hasSrid = (type & 0x20000000) !== 0
    if (geometryType !== 1) return null

    const coordinateOffset = 5 + (hasSrid ? 4 : 0)
    if (bytes.length < coordinateOffset + 16) return null

    return parseFinitePoint(
      view.getFloat64(coordinateOffset, littleEndian),
      view.getFloat64(coordinateOffset + 8, littleEndian),
    )
  }

  const parseWktPoint = (value: string) => {
    const match = value
      .trim()
      .match(/^(?:SRID=\d+;)?POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)$/i)
    if (!match) return null
    return parseFinitePoint(Number(match[1]), Number(match[2]))
  }

  try {
    if (typeof raw === 'string') {
      const fromWkb = parseWkbHexPoint(raw)
      if (fromWkb) return fromWkb

      const fromWkt = parseWktPoint(raw)
      if (fromWkt) return fromWkt
    }

    const geo = typeof raw === 'string' ? JSON.parse(raw) : raw
    // Tighten the shape check before returning. The
    // previous version assumed `coordinates[0]` and `[1]` were numbers
    // when only checking `Array.isArray(coordinates)` — a malformed
    // GeoJSON like `{ type: 'Point', coordinates: ['12.4', null] }`
    // would silently propagate non-numbers to consumers expecting
    // `{ lng: number, lat: number }`. Validate length AND that both
    // coordinates parse as finite numbers before returning.
    if (geo?.type === 'Point' && Array.isArray(geo.coordinates) && geo.coordinates.length === 2) {
      const lng = Number(geo.coordinates[0])
      const lat = Number(geo.coordinates[1])
      return parseFinitePoint(lng, lat)
    }
  } catch {
    // Invalid GeoJSON
  }
  return null
}

// ─── Row mappers ────────────────────────────────────────────────────────────

type NearbyRow = RpcRow<'nearby_vehicles'>
type SearchRow = RpcRow<'search_vehicles'>

function mapNearby(row: NearbyRow): NearbyVehicle {
  return {
    id: row.id,
    hostId: row.host_id,
    title: formatMarketTransmissionText(row.title),
    makeName: row.make_name,
    modelName: row.model_name,
    year: row.year,
    dailyRateCents: row.daily_rate_cents,
    hourlyRateCents: row.hourly_rate_cents ?? null,
    category: row.category,
    transmission: row.transmission,
    status: row.status,
    distanceKm: row.distance_km,
    homeLocation: parsePostGISPoint(row.home_location),
    primaryImageUrl: row.primary_image_url,
    primaryImageBlurhash: row.primary_image_blurhash,
    ratingAvg: row.rating_avg,
    ratingCount: row.rating_count,
    rentedDays: row.rented_days,
    isInstantBook: row.is_instant_book,
  }
}

function mapSearch(row: SearchRow): SearchVehicle {
  return {
    ...mapNearby(row),
    shadowRankPenalty: Number(row.shadow_rank_penalty ?? 0),
  }
}

// ─── Host vehicle row mapper ────────────────────────────────────────────────

/**
 * Shape returned by `HOST_VEHICLE_SELECT` — every `vehicles` column (`*`)
 * plus the four embeds that select asks for.
 */
type HostVehicleRow = WithEmbed<
  Row<'vehicles'>,
  {
    vehicle_makes: EmbedOne<'vehicle_makes', 'name'>
    vehicle_models: EmbedOne<'vehicle_models', 'name'>
    vehicle_images: EmbedMany<
      'vehicle_images',
      | 'id'
      | 'vehicle_id'
      | 'storage_path'
      | 'url'
      | 'blurhash'
      | 'position'
      | 'is_primary'
      | 'width'
      | 'height'
      | 'capture_source'
      | 'captured_at'
      | 'requirement_id'
      | 'photo_sha256'
    >
    vehicle_documents: EmbedMany<
      'vehicle_documents',
      | 'id'
      | 'vehicle_id'
      | 'document_type'
      | 'document_number'
      | 'expiry_date'
      | 'status'
      | 'rejection_reason'
      | 'created_at'
      | 'updated_at'
    >
  }
>

/** Shape returned by `DETAIL_SELECT` in `getVehicleDetail`. */
type VehicleDetailRow = WithEmbed<
  Cols<
    'vehicles',
    | 'id'
    | 'title'
    | 'description'
    | 'year'
    | 'category'
    | 'color'
    | 'transmission'
    | 'fuel_type'
    | 'seats'
    | 'doors'
    | 'daily_rate_cents'
    | 'hourly_rate_cents'
    | 'weekly_discount_percent'
    | 'monthly_discount_percent'
    | 'security_deposit_cents'
    | 'cleaning_fee_cents'
    | 'daily_km_limit'
    | 'excess_km_rate_cents'
    | 'min_rental_hours'
    | 'home_location'
    | 'home_address'
    // Ownership is deliberately ABSENT here. This is the guest-facing detail row, and the
    // declaration names a private person — whoever authorised the host to rent their car out.
    // It belongs to the host and the insurer reviewing them, and `HostVehicleRow` carries it.
    | 'delivery_radius_km'
    | 'is_instant_book'
    | 'status'
    | 'features'
    | 'insurance_model'
    | 'coverage_path'
    | 'coverage_review_status'
    | 'coverage_daily_rate_cents'
    | 'organization_id'
    | 'rules'
    | 'rating_avg'
    | 'rating_count'
    | 'rented_days'
    | 'published_at'
    | 'created_at'
    | 'host_id'
  >,
  {
    vehicle_makes: EmbedOne<'vehicle_makes', 'name'>
    vehicle_models: EmbedOne<'vehicle_models', 'name'>
    organizations: EmbedOne<'organizations', 'name'>
    vehicle_images: EmbedMany<
      'vehicle_images',
      'id' | 'url' | 'blurhash' | 'position' | 'is_primary' | 'width' | 'height' | 'requirement_id'
    >
  }
>

/** Guest-authored trip experience projected onto a vehicle by Postgres. */
type VehicleDetailReviewRow = RpcRow<'get_vehicle_trip_reviews'>

function mapHostVehicle(row: HostVehicleRow): HostVehicle {
  return {
    id: row.id,
    hostId: row.host_id,
    title: formatMarketTransmissionText(row.title),
    description: row.description ? formatMarketTransmissionText(row.description) : null,
    makeId: row.make_id,
    makeName: row.vehicle_makes?.name ?? '',
    modelId: row.model_id,
    modelName: row.vehicle_models?.name ?? '',
    year: row.year,
    category: row.category,
    color: row.color,
    transmission: row.transmission,
    fuelType: row.fuel_type,
    seats: row.seats,
    doors: row.doors,
    plateNumber: row.plate_number,
    plateCountry: row.plate_country,
    vin: row.vin,
    odometerKm: row.odometer_km,
    dailyRateCents: row.daily_rate_cents,
    hourlyRateCents: row.hourly_rate_cents,
    weeklyDiscountPercent: row.weekly_discount_percent,
    monthlyDiscountPercent: row.monthly_discount_percent,
    securityDepositCents: row.security_deposit_cents,
    cleaningFeeCents: row.cleaning_fee_cents,
    dailyKmLimit: row.daily_km_limit,
    excessKmRateCents: row.excess_km_rate_cents,
    minRentalHours: normalizeVehicleMinRentalHours(row.min_rental_hours),
    homeLocation: parsePostGISPoint(row.home_location),
    homeAddress: row.home_address,
    // NULL means the question was never asked — a listing older than the column. It is read
    // back as `null`, never as a default, so nothing downstream can mistake our silence for
    // the host having answered "mine".
    ownership: row.ownership_type
      ? {
          type: row.ownership_type as VehicleOwnershipType,
          authorizedByName: row.ownership_authorized_by_name ?? undefined,
          relationship: row.ownership_relationship ?? undefined,
          declaredAt: row.ownership_declared_at ?? undefined,
        }
      : null,
    countryId: row.country_id,
    cityId: row.city_id,
    deliveryRadiusKm: row.delivery_radius_km,
    ratingAvg: row.rating_avg,
    ratingCount: row.rating_count,
    rentedDays: row.rented_days,
    isInstantBook: row.is_instant_book,
    status: row.status,
    features: row.features ?? [],
    insuranceModel: row.insurance_model,
    coverageReviewStatus: row.coverage_review_status ?? null,
    coveragePath: row.coverage_path,
    organizationId: row.organization_id,
    companyOwnerDetected: row.company_owner_detected ?? false,
    detectedLegalName: row.detected_legal_name,
    rules: row.rules,
    publishedAt: row.published_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    images: sortVehicleImagesForShowcase(
      (row.vehicle_images ?? []).map((img) => ({
        id: img.id,
        vehicleId: img.vehicle_id,
        storagePath: img.storage_path,
        url: img.url,
        blurhash: img.blurhash,
        position: img.position,
        isPrimary: img.is_primary,
        width: img.width,
        height: img.height,
        // Provenance is required so edit mode can rehydrate the 17 Qualitas
        // slots without forcing a full re-shoot for a price change.
        captureSource: img.capture_source ?? null,
        capturedAt: img.captured_at ?? null,
        requirementId: img.requirement_id ?? null,
        photoSha256: img.photo_sha256 ?? null,
        verificationPayload: {},
      })),
    ),
    documents: (row.vehicle_documents ?? [])
      .slice()
      .sort(
        (a, b) =>
          new Date(b.updated_at ?? b.created_at).getTime() -
          new Date(a.updated_at ?? a.created_at).getTime(),
      )
      .map((doc) => ({
        id: doc.id,
        vehicleId: doc.vehicle_id,
        documentType: doc.document_type,
        documentNumber: doc.document_number,
        expiryDate: doc.expiry_date,
        status: doc.status,
        rejectionReason: doc.rejection_reason,
        createdAt: doc.created_at,
        updatedAt: doc.updated_at,
      })),
  }
}

// ─── Country resolver (ISO code → ULID, cached) ─────────────────────────────

// Cache only the resolved string, not the Promise.
// The previous version stored `Promise<string>` and on rejection called
// `countryCache.delete(key)` *inside* the IIFE — but the rejection
// propagated to every concurrent caller awaiting the cached promise.
// The set call at the bottom (`countryCache.set(key, promise)`) ran
// AFTER the await, so for true concurrent calls there was a race:
// the second caller could find the rejected promise still in the cache
// and re-throw the stale error even though the network had recovered.
// Now we only cache successful resolves, and concurrent callers race
// to populate the cache (worst case: a few duplicate DB queries on
// first request, never a stuck failure).
const countryCache = new Map<string, string>()

async function resolveCountryId(codeOrId: string): Promise<string> {
  // Already a ULID (26+ chars) — pass through
  if (codeOrId.length > 10) return codeOrId

  const key = codeOrId.toUpperCase()
  const cached = countryCache.get(key)
  if (cached) return cached

  const { data, error } = await supabase.from('countries').select('id').eq('code', key).single()

  if (error || !data) {
    // Don't cache the failure — next caller can retry cleanly.
    throw ServiceError.notFound(`Country with code '${codeOrId}'`)
  }

  countryCache.set(key, data.id)
  return data.id
}

// ─── Input mapper (camelCase → snake_case) ──────────────────────────────────

function mapCreateInput(input: CreateVehicleInput, hostId: string, countryId: string) {
  const minRentalHours = normalizeVehicleMinRentalHours(input.minRentalHours)
  if (input.minRentalHours != null && minRentalHours === null) {
    throw new ServiceError({
      message: 'Invalid vehicle minimum rental duration',
      code: 'VEHICLE_MIN_RENTAL_INVALID',
      statusCode: 422,
      userMessageKey: 'listing.validation.min_rental_invalid',
    })
  }

  return {
    host_id: hostId,
    title: input.title,
    description: input.description ?? null,
    make_id: input.makeId,
    model_id: input.modelId,
    year: input.year,
    category: input.category,
    color: input.color,
    transmission: input.transmission,
    fuel_type: input.fuelType,
    seats: input.seats,
    doors: input.doors,
    plate_number: normalizePlate(input.plateNumber),
    plate_country: input.plateCountry ?? 'SV',
    vin: input.vin ?? null,
    odometer_km: input.odometerKm ?? 0,
    daily_rate_cents: input.dailyRateCents,
    hourly_rate_cents: input.hourlyRateCents ?? null,
    weekly_discount_percent: input.weeklyDiscountPercent ?? null,
    monthly_discount_percent: input.monthlyDiscountPercent ?? null,
    // Optional host-set security deposit (re-enabled 2026-08-16). Charged to the
    // guest, held by Tago, returned in full after the rental.
    security_deposit_cents: input.securityDepositCents ?? 0,
    cleaning_fee_cents: input.cleaningFeeCents ?? null,
    daily_km_limit: input.dailyKmLimit ?? null,
    excess_km_rate_cents: input.excessKmRateCents ?? null,
    min_rental_hours: minRentalHours,
    home_address: input.homeAddress ?? null,
    home_location:
      input.homeLat != null && input.homeLng != null
        ? `POINT(${input.homeLng} ${input.homeLat})`
        : null,
    country_id: countryId,
    city_id: input.cityId ?? null,
    delivery_radius_km: input.deliveryRadiusKm ?? null,
    is_instant_book: input.isInstantBook ?? false,
    features: input.features ?? [],
    insurance_model: input.insuranceModel ?? EARLY_ACCESS.hostInsuranceModel,
    rules: input.rules ?? null,
    organization_id: input.organizationId ?? null,
    company_owner_detected: input.companyOwnerDetected ?? false,
    detected_legal_name: input.detectedLegalName ?? null,
    ...mapOwnershipDeclaration(input.ownership),
    status: 'draft' as const,
  }
}

/**
 * The host's answer, on its way to the columns that keep it.
 *
 * `declaredAt` is stamped here rather than trusted from the client: it is the one field a
 * reviewer uses to tell a fresh declaration from one an edit carried along, so it has to mean
 * "when the row was written", not "what the device's clock said".
 *
 * Only `third_party` carries an authoriser. Leaving a stale name attached to a car the host
 * later said was their own would leave a person named as having permitted something that,
 * by the host's own corrected answer, never needed permission.
 */
function mapOwnershipDeclaration(ownership: VehicleOwnershipDeclaration | undefined) {
  if (!ownership) return {}
  const isThirdParty = ownership.type === 'third_party'

  return {
    ownership_type: ownership.type,
    ownership_authorized_by_name: isThirdParty ? ownership.authorizedByName?.trim() || null : null,
    ownership_relationship: isThirdParty ? ownership.relationship?.trim() || null : null,
    ownership_declared_at: new Date().toISOString(),
  }
}

function assertEarlyAccessCreateInput(input: CreateVehicleInput): void {
  if (
    (input.insuranceModel ?? EARLY_ACCESS.hostInsuranceModel) !== EARLY_ACCESS.hostInsuranceModel
  ) {
    throw new ServiceError({
      message: 'Por ahora, todas las publicaciones deben usar póliza propia verificada.',
      code: 'EARLY_ACCESS_INSURANCE_LOCKED',
      statusCode: 403,
      userMessageKey: 'listing.validation.insurance_agency_only',
    })
  }
}

function mapUpdateInput(input: UpdateVehicleInput): UpdateRow<'vehicles'> {
  const mapped: UpdateRow<'vehicles'> = {}
  if (input.title !== undefined) mapped.title = input.title
  if (input.description !== undefined) mapped.description = input.description ?? null
  if (input.makeId !== undefined) mapped.make_id = input.makeId
  if (input.modelId !== undefined) mapped.model_id = input.modelId
  if (input.year !== undefined) mapped.year = input.year
  if (input.category !== undefined) mapped.category = input.category
  if (input.color !== undefined) mapped.color = input.color
  if (input.transmission !== undefined) mapped.transmission = input.transmission
  if (input.fuelType !== undefined) mapped.fuel_type = input.fuelType
  if (input.seats !== undefined) mapped.seats = input.seats
  if (input.doors !== undefined) mapped.doors = input.doors
  if (input.plateNumber !== undefined) mapped.plate_number = normalizePlate(input.plateNumber)
  if (input.plateCountry !== undefined) mapped.plate_country = input.plateCountry
  if (input.vin !== undefined) mapped.vin = input.vin ?? null
  if (input.odometerKm !== undefined) mapped.odometer_km = input.odometerKm
  if (input.dailyRateCents !== undefined) mapped.daily_rate_cents = input.dailyRateCents
  if (input.hourlyRateCents !== undefined) mapped.hourly_rate_cents = input.hourlyRateCents ?? null
  if (input.weeklyDiscountPercent !== undefined)
    mapped.weekly_discount_percent = input.weeklyDiscountPercent ?? null
  if (input.monthlyDiscountPercent !== undefined)
    mapped.monthly_discount_percent = input.monthlyDiscountPercent ?? null
  // Optional host-set security deposit (re-enabled 2026-08-16): only touched
  // when the input carries it, so a partial update never zeroes a set deposit.
  if (input.securityDepositCents !== undefined)
    mapped.security_deposit_cents = input.securityDepositCents ?? 0
  if (input.cleaningFeeCents !== undefined)
    mapped.cleaning_fee_cents = input.cleaningFeeCents ?? null
  if (input.dailyKmLimit !== undefined) mapped.daily_km_limit = input.dailyKmLimit ?? null
  if (input.excessKmRateCents !== undefined)
    mapped.excess_km_rate_cents = input.excessKmRateCents ?? null
  if (input.minRentalHours !== undefined) {
    const minRentalHours = normalizeVehicleMinRentalHours(input.minRentalHours)
    if (input.minRentalHours != null && minRentalHours === null) {
      throw new ServiceError({
        message: 'Invalid vehicle minimum rental duration',
        code: 'VEHICLE_MIN_RENTAL_INVALID',
        statusCode: 422,
        userMessageKey: 'listing.validation.min_rental_invalid',
      })
    }
    mapped.min_rental_hours = minRentalHours
  }
  if (input.homeAddress !== undefined) mapped.home_address = input.homeAddress ?? null
  // Absent means "this save is not about ownership" — the stored declaration stays untouched.
  // Only an actual answer overwrites one.
  if (input.ownership !== undefined) Object.assign(mapped, mapOwnershipDeclaration(input.ownership))
  if (input.homeLat != null && input.homeLng != null)
    mapped.home_location = `POINT(${input.homeLng} ${input.homeLat})`
  // countryId is resolved async by updateVehicle before calling this mapper
  if (input.countryId !== undefined) mapped.country_id = input.countryId
  if (input.cityId !== undefined) mapped.city_id = input.cityId ?? null
  if (input.deliveryRadiusKm !== undefined)
    mapped.delivery_radius_km = input.deliveryRadiusKm ?? null
  if (input.isInstantBook !== undefined) mapped.is_instant_book = input.isInstantBook
  if (input.features !== undefined) mapped.features = input.features
  if (input.insuranceModel !== undefined) mapped.insurance_model = input.insuranceModel
  if (input.rules !== undefined) mapped.rules = input.rules ?? null
  if (input.organizationId !== undefined) mapped.organization_id = input.organizationId ?? null
  if (input.companyOwnerDetected !== undefined)
    mapped.company_owner_detected = input.companyOwnerDetected
  if (input.detectedLegalName !== undefined)
    mapped.detected_legal_name = input.detectedLegalName ?? null
  return mapped
}

// ─── Host vehicle select query ──────────────────────────────────────────────

// PostgREST 400s on multi-line select strings (newlines URL-encode to %0A
// and the server occasionally truncates mid-parse). Keep single-line.
// Host dashboards only need visual image fields. Live-photo provenance is
// enforced through vehicle-image.service, so keeping this select legacy avoids
// noisy 400s while older environments finish applying the ASR photo migration.
const HOST_VEHICLE_SELECT =
  '*,vehicle_makes(name),vehicle_models(name),vehicle_images(id,vehicle_id,storage_path,url,blurhash,position,is_primary,width,height,capture_source,captured_at,requirement_id,photo_sha256),vehicle_documents(id,vehicle_id,document_type,document_number,expiry_date,status,rejection_reason,created_at,updated_at)'

function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>()
  const result: T[] = []
  for (const item of items) {
    if (seen.has(item.id)) continue
    seen.add(item.id)
    result.push(item)
  }
  return result
}

function normalizeCatalogLookupName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

function dedupeCatalogLookups<T extends { id: string; name: string }>(items: T[]): T[] {
  const seen = new Set<string>()
  const result: T[] = []
  for (const item of items) {
    const key = normalizeCatalogLookupName(item.name)
    if (seen.has(key)) continue
    seen.add(key)
    result.push(item)
  }
  return result
}

// ─── Service ────────────────────────────────────────────────────────────────

class VehicleServiceImpl {
  private hostVehiclesQuery(hostId: string, organizationId?: string | null) {
    let query = supabase
      .from('vehicles')
      .select(HOST_VEHICLE_SELECT)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    query = organizationId
      ? query.eq('organization_id', organizationId)
      : query.eq('host_id', hostId)

    return query
  }

  private vehicleByIdQuery(vehicleId: string) {
    return supabase
      .from('vehicles')
      .select(HOST_VEHICLE_SELECT)
      .eq('id', vehicleId)
      .is('deleted_at', null)
      .single()
  }

  async getNearbyVehicles(
    lat: number,
    lng: number,
    radiusKm = 10,
    limit = 20,
  ): Promise<NearbyVehicle[]> {
    const { data, error } = await supabase.rpc('nearby_vehicles', {
      lat,
      lng,
      radius_km: radiusKm,
      limit_count: limit,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    return dedupeById(data?.map(mapNearby) ?? [])
  }

  async searchVehicles(filters: VehicleSearchFilters): Promise<SearchVehicle[]> {
    const { data, error } = await supabase.rpc('search_vehicles', {
      p_search_query: filters.query,
      p_category: filters.category,
      p_transmission: filters.transmission,
      p_start_date: filters.startDate,
      p_end_date: filters.endDate,
      p_lat: filters.lat,
      p_lng: filters.lng,
      p_radius_km: filters.radiusKm ?? 50,
      p_min_price: filters.minPrice,
      p_max_price: filters.maxPrice,
      p_min_year: filters.minYear,
      p_required_features: filters.requiredFeatures,
      p_limit: filters.limit ?? 50,
      p_offset: filters.offset ?? 0,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    return dedupeById(data?.map(mapSearch) ?? [])
  }

  async getFavoriteIds(userId: string): Promise<Set<string>> {
    const { data, error } = await supabase
      .from('favorites')
      .select('vehicle_id')
      .eq('profile_id', userId)

    if (error) throw ServiceError.fromSupabaseError(error)
    return new Set((data ?? []).map((r) => r.vehicle_id))
  }

  async toggleFavorite(userId: string, vehicleId: string): Promise<boolean> {
    // Try delete first
    const { data: deleted } = await supabase
      .from('favorites')
      .delete()
      .match({ profile_id: userId, vehicle_id: vehicleId })
      .select('id')

    if (deleted && deleted.length > 0) return false // Was favorited, now removed

    // Not found — insert
    const { error } = await supabase
      .from('favorites')
      .insert({ profile_id: userId, vehicle_id: vehicleId })

    if (error) throw ServiceError.fromSupabaseError(error)
    return true // Now favorited
  }

  // ── Host CRUD ───────────────────────────────────────────────────────────

  async getHostVehicles(hostId: string, organizationId?: string | null): Promise<HostVehicle[]> {
    const { data, error } = await this.hostVehiclesQuery(hostId, organizationId)

    if (error) throw ServiceError.fromSupabaseError(error)
    return dedupeById((data ?? []).map(mapHostVehicle))
  }

  /**
   * Blockers de publicación de los vehículos del llamador, decididos por el
   * gate canónico del servidor (`list_my_vehicle_listing_blockers`). La app NO
   * re-implementa la regla — la traduce a texto. Devuelve los mismos códigos
   * que la compuerta de activación, la reserva y la notificación.
   */
  async getMyListingBlockers(): Promise<Array<{ vehicleId: string; blockers: string[] }>> {
    const { data, error } = await supabase.rpc('list_my_vehicle_listing_blockers')

    if (error) throw ServiceError.fromSupabaseError(error)
    return ((data ?? []) as Array<{ vehicle_id: string; blockers: string[] | null }>).map(
      (row) => ({ vehicleId: row.vehicle_id, blockers: row.blockers ?? [] }),
    )
  }

  async getVehicleById(vehicleId: string): Promise<HostVehicle> {
    const { data, error } = await this.vehicleByIdQuery(vehicleId)

    if (error) throw ServiceError.fromSupabaseError(error)
    return mapHostVehicle(data)
  }

  async createVehicle(hostId: string, input: CreateVehicleInput): Promise<HostVehicle> {
    assertEarlyAccessCreateInput(input)
    // Own the vehicle by the LIVE session identity, never the hook-cached id the
    // caller passed. A drifted host_id makes vehicles.host_id != auth.uid(),
    // which then fails the storage RLS on EVERY photo upload for that car (the
    // 'vehicles' bucket checks vehicle ownership). host_id is always the
    // authenticated user; agency listings carry organization_id separately.
    void hostId
    const liveHostId = await requireLiveUserId()
    const countryId = await resolveCountryId(input.countryId)
    const row = mapCreateInput(input, liveHostId, countryId)

    const { data, error } = await supabase
      .from('vehicles')
      .insert(row)
      .select(HOST_VEHICLE_SELECT)
      .single()

    if (error) {
      const message = error.message.toLowerCase()
      if (error.code === '23505' && message.includes('plate')) {
        const reusable = await this.findReusablePlateDraft(
          liveHostId,
          row.plate_number,
          row.plate_country,
          input.organizationId ?? null,
        )
        if (reusable) {
          return this.updateVehicle(reusable.id, {
            ...input,
            countryId,
            plateNumber: row.plate_number,
            plateCountry: row.plate_country,
          })
        }

        throw new ServiceError({
          message: error.message,
          code: 'VEHICLE_PLATE_EXISTS',
          statusCode: 409,
          userMessageKey: 'listing.validation.plate_exists',
          cause: error,
        })
      }
      throw ServiceError.fromSupabaseError(error)
    }
    return mapHostVehicle(data)
  }

  private async findReusablePlateDraft(
    hostId: string,
    plateNumber: string,
    plateCountry: string,
    organizationId: string | null,
  ): Promise<HostVehicle | null> {
    const { data, error } = await supabase
      .from('vehicles')
      .select(HOST_VEHICLE_SELECT)
      .eq('plate_number', plateNumber)
      .eq('plate_country', plateCountry)
      .is('deleted_at', null)
      .limit(5)

    if (error || !data?.length) return null

    const rows = data.map(mapHostVehicle)
    const reusable = rows.find((vehicle) => {
      if (vehicle.status === 'active') return false
      if (organizationId) return vehicle.organizationId === organizationId
      return vehicle.hostId === hostId && vehicle.organizationId == null
    })

    return reusable ?? null
  }

  async updateVehicle(vehicleId: string, input: UpdateVehicleInput): Promise<HostVehicle> {
    if (
      input.insuranceModel !== undefined &&
      input.insuranceModel !== EARLY_ACCESS.hostInsuranceModel
    ) {
      throw new ServiceError({
        message: 'Por ahora, todas las publicaciones deben usar póliza propia verificada.',
        code: 'EARLY_ACCESS_INSURANCE_LOCKED',
        statusCode: 403,
        userMessageKey: 'listing.validation.insurance_agency_only',
      })
    }

    // Resolve country code → ULID before mapping
    if (input.countryId !== undefined) {
      input = { ...input, countryId: await resolveCountryId(input.countryId) }
    }
    const mapped = mapUpdateInput(input)
    if (Object.keys(mapped).length === 0) {
      return this.getVehicleById(vehicleId)
    }

    const { data, error } = await supabase
      .from('vehicles')
      .update(mapped)
      .eq('id', vehicleId)
      .select(HOST_VEHICLE_SELECT)
      .single()

    if (error) throw ServiceError.fromSupabaseError(error)
    return mapHostVehicle(data)
  }

  async deleteVehicle(vehicleId: string): Promise<void> {
    // Soft delete — set deleted_at + status
    const { error } = await supabase
      .from('vehicles')
      .update({ deleted_at: new Date().toISOString(), status: 'archived' })
      .eq('id', vehicleId)

    if (error) throw ServiceError.fromSupabaseError(error)
  }

  async publishVehicle(vehicleId: string): Promise<void> {
    await this.setVehicleStatus(vehicleId, 'active')
  }

  async setVehicleStatus(
    vehicleId: string,
    status: 'draft' | 'pending_review' | 'active' | 'paused',
  ): Promise<'draft' | 'pending_review' | 'active' | 'paused'> {
    const update: UpdateRow<'vehicles'> = {
      status,
    }

    if (status === 'active' || status === 'pending_review') {
      update.published_at = new Date().toISOString()
      update.deleted_at = null
    }

    const { data, error } = await supabase
      .from('vehicles')
      .update(update)
      .eq('id', vehicleId)
      .select('status')
      .single()

    if (error) throw ServiceError.fromSupabaseError(error)
    return data.status as 'draft' | 'pending_review' | 'active' | 'paused'
  }

  // ── Lookups ─────────────────────────────────────────────────────────────

  async getMakes(): Promise<VehicleMakeLookup[]> {
    const { data, error } = await supabase
      .from('vehicle_makes')
      .select('id, name, logo_url')
      .eq('is_active', true)
      .order('name')

    if (error) throw ServiceError.fromSupabaseError(error)
    return dedupeCatalogLookups(
      (data ?? []).map((r) => ({
        id: r.id,
        name: r.name,
        logoUrl: r.logo_url,
      })),
    )
  }

  async getModels(makeId: string): Promise<VehicleModelLookup[]> {
    const { data, error } = await supabase
      .from('vehicle_models')
      .select(
        'id, make_id, name, category, default_seats, default_doors, default_transmission, default_fuel_type, default_features',
      )
      .eq('make_id', makeId)
      .eq('is_active', true)
      .order('name')

    if (error) throw ServiceError.fromSupabaseError(error)
    return dedupeCatalogLookups(
      (data ?? []).map((r) => ({
        id: r.id,
        makeId: r.make_id,
        name: r.name,
        category: r.category as VehicleModelLookup['category'],
        defaultSeats: r.default_seats,
        defaultDoors: r.default_doors,
        defaultTransmission: r.default_transmission as VehicleModelLookup['defaultTransmission'],
        defaultFuelType: r.default_fuel_type as VehicleModelLookup['defaultFuelType'],
        defaultFeatures: r.default_features,
      })),
    )
  }
  /**
   * Guest-facing vehicle detail with host profile, images, and reviews.
   * Parallel queries for host profile + reviews to minimize latency.
   */
  async getVehicleDetail(vehicleId: string): Promise<VehicleDetail> {
    const DETAIL_SELECT =
      'id,title,description,year,category,color,transmission,fuel_type,seats,doors,daily_rate_cents,hourly_rate_cents,weekly_discount_percent,monthly_discount_percent,security_deposit_cents,cleaning_fee_cents,daily_km_limit,excess_km_rate_cents,min_rental_hours,home_location,home_address,delivery_radius_km,is_instant_book,status,features,insurance_model,coverage_path,coverage_review_status,coverage_daily_rate_cents,organization_id,rules,rating_avg,rating_count,rented_days,published_at,created_at,host_id,vehicle_makes!vehicles_make_id_fkey(name),vehicle_models!vehicles_model_id_fkey(name),organizations!vehicles_organization_id_fkey(name),vehicle_images(id,url,blurhash,position,is_primary,width,height,requirement_id)'

    const { data: vehicle, error: vehicleError } = await supabase
      .from('vehicles')
      .select(DETAIL_SELECT)
      .eq('id', vehicleId)
      .in('status', ['active', 'pending_review', 'draft'])
      .is('deleted_at', null)
      .maybeSingle()

    if (vehicleError) throw ServiceError.fromSupabaseError(vehicleError)
    if (!vehicle) throw ServiceError.notFound('Vehicle')

    // Naming the row type makes the select string and the mapper agree at
    // compile time: adding a field below without adding the column to
    // DETAIL_SELECT is a type error, not a silent `undefined` at runtime.
    const v: VehicleDetailRow = vehicle
    const hostId = v.host_id

    // Parallel: host profile + the canonical trip-review projection.
    const [hostResult, reviewsResult] = await Promise.all([
      supabase
        .from('profiles')
        .select(
          'id, display_name, avatar_url, host_rating_avg, host_rating_count, is_verified, created_at',
        )
        .eq('id', hostId)
        .maybeSingle(),
      supabase.rpc('get_vehicle_trip_reviews', {
        p_vehicle_id: vehicleId,
        p_limit: 10,
      }),
    ])

    if (hostResult.error) throw ServiceError.fromSupabaseError(hostResult.error)
    if (!hostResult.data) throw ServiceError.notFound('Vehicle host')
    if (reviewsResult.error) throw ServiceError.fromSupabaseError(reviewsResult.error)

    const host: VehicleHostSummary = {
      id: hostResult.data.id,
      displayName: hostResult.data.display_name,
      avatarUrl: hostResult.data.avatar_url,
      hostRatingAvg: hostResult.data.host_rating_avg,
      hostRatingCount: hostResult.data.host_rating_count,
      isVerified: hostResult.data.is_verified,
      createdAt: hostResult.data.created_at,
    }

    const images: VehicleDetailImage[] = sortVehicleImagesForShowcase(
      (v.vehicle_images ?? []).map((img) => ({
        id: img.id,
        url: img.url,
        blurhash: img.blurhash,
        position: img.position,
        isPrimary: img.is_primary,
        width: img.width,
        height: img.height,
        requirementId: img.requirement_id ?? null,
      })),
    )

    const rawReviews: VehicleDetailReviewRow[] = reviewsResult.data ?? []

    const reviews: VehicleReviewItem[] = rawReviews.map((r) => ({
      id: r.id,
      rating: r.rating,
      comment: r.comment,
      reviewerDisplayName: r.reviewer_display_name ?? null,
      reviewerAvatarUrl: r.reviewer_avatar_url ?? null,
      createdAt: r.created_at,
    }))

    return {
      id: v.id,
      title: formatMarketTransmissionText(v.title),
      description: formatMarketTransmissionText(v.description) || null,
      makeName: v.vehicle_makes?.name ?? '',
      modelName: v.vehicle_models?.name ?? '',
      year: v.year,
      category: v.category,
      color: v.color,
      transmission: v.transmission,
      fuelType: v.fuel_type,
      seats: v.seats,
      doors: v.doors,
      dailyRateCents: v.daily_rate_cents,
      hourlyRateCents: v.hourly_rate_cents,
      weeklyDiscountPercent: v.weekly_discount_percent,
      monthlyDiscountPercent: v.monthly_discount_percent,
      securityDepositCents: v.security_deposit_cents,
      cleaningFeeCents: v.cleaning_fee_cents,
      dailyKmLimit: v.daily_km_limit,
      excessKmRateCents: v.excess_km_rate_cents,
      minRentalHours: normalizeVehicleMinRentalHours(v.min_rental_hours),
      homeLocation: parsePostGISPoint(v.home_location),
      homeAddress: v.home_address,
      deliveryRadiusKm: v.delivery_radius_km,
      isInstantBook: v.is_instant_book,
      status: v.status,
      features: v.features ?? [],
      insuranceModel: v.insurance_model,
      coverageReviewStatus: v.coverage_review_status ?? null,
      coveragePath: v.coverage_path,
      coverageDailyRateCents: v.coverage_daily_rate_cents,
      organizationId: v.organization_id,
      organizationName: v.organizations?.name ?? null,
      rules: v.rules,
      ratingAvg: v.rating_avg,
      ratingCount: v.rating_count,
      rentedDays: v.rented_days,
      publishedAt: v.published_at,
      createdAt: v.created_at,
      host,
      images,
      reviews,
      // `reviews` está limitado a diez para la vista previa; el agregado del
      // vehículo contiene el total canónico de experiencias de sus huéspedes.
      reviewCount: v.rating_count,
    }
  }
}

export const vehicleService = new VehicleServiceImpl()
